'use client';

import { useEffect, useRef } from 'react';
import { useUser } from '@auth0/nextjs-auth0/client';
import { usePathname } from 'next/navigation';
import { initClarity, identifyUser } from '@/config/clarity.config';
const SESSION_STORAGE_KEY = '_intalent_session_id';
function getOrCreateSessionId() {
  try {
    if (typeof window === 'undefined') return '';
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID?.() ?? `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return '';
  }
}

/**
 * Client-only component that initializes Microsoft Clarity analytics.
 * Renders nothing; must be mounted in a client component tree (e.g. root layout).
 */
export function ClarityInit() {
  useEffect(() => {
    try {
      initClarity();
    } catch {
      // Fail silently
    }
  }, []);
  return null;
}

/**
 * Calls Clarity.identify when user or pathname changes.
 * Must be mounted inside Auth0 UserProvider.
 */
export function ClarityIdentify() {
  const {
    user
  } = useUser();
  const pathname = usePathname();
  const sessionIdRef = useRef(null);
  useEffect(() => {
    try {
      const customId = user?.sub ?? 'anonymous';
      const customSessionId = sessionIdRef.current ?? getOrCreateSessionId();
      sessionIdRef.current = customSessionId;
      const customPageId = pathname ?? '';
      const friendlyName = user?.name ?? user?.email ?? undefined;
      identifyUser(customId, customSessionId, customPageId, friendlyName);
    } catch {
      // Fail silently
    }
  }, [user?.sub, user?.email, user?.name, pathname]);
  return null;
}